import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import { fmtDateTime } from "../campanhasApi";
import {
  ADJUST_SHORTCUTS,
  adjustCreativeApi,
  archiveCreativeApi,
  changeTextsApi,
  cleanContent,
  creativeFileName,
  downloadCreative,
  getCreativeApi,
  redoCreativeApi,
  storiesCreativeApi,
  type CreativeContent,
  type CreativeDetail,
  type GeneratedCreative,
} from "../criativosApi";
import { CreativeContentForm, CreativeImage, GeneratingNotice, ReviewBox } from "../components/CreativeParts";
import UseCreativeAsAdModal from "../components/UseCreativeAsAdModal";
import { CAMPANHA_PAGE, CAMPANHAS_PAGE, CRIATIVO_PAGE, CRIATIVOS_PAGE } from "../routes";
import "./Campanhas.css";
import "./Criativos.css";

type Tool = "ajustar" | "textos" | "mais";

const TOOLS: { value: Tool; label: string }[] = [
  { value: "ajustar", label: "Ajustar a imagem" },
  { value: "textos", label: "Trocar os textos" },
  { value: "mais", label: "Stories e refazer" },
];

const titleOf = (c: CreativeContent) => [c.roleTitle, c.roleSubtitle].filter(Boolean).join(" ") || "Criativo";

/** Estúdio do criativo: cada pedido gera uma versão nova; as anteriores ficam. */
const CriativoPage: React.FC = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack(CRIATIVOS_PAGE());
  const [detail, setDetail] = useState<CreativeDetail | null>(null);
  const [tool, setTool] = useState<Tool>("ajustar");
  const [instruction, setInstruction] = useState("");
  const [texts, setTexts] = useState<CreativeContent | null>(null);
  const [photoPrompt, setPhotoPrompt] = useState("");
  const [working, setWorking] = useState<string | null>(null);
  const [useAsAd, setUseAsAd] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const load = useCallback(() => {
    getCreativeApi(id)
      .then((d) => {
        setDetail(d);
        const current = d.versions.find((v) => v.id === id) ?? d.versions[d.versions.length - 1];
        setTexts(current.content);
        setPhotoPrompt(current.photoPrompt ?? "");
      })
      .catch((e) => toast.error(e?.message));
  }, [id]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  if (!detail || !texts) {
    return (
      <div className="cmpPage">
        <main className="cmpPage__main">
          <p className="cmpLoading">Carregando...</p>
        </main>
      </div>
    );
  }

  const current = detail.versions.find((v) => v.id === id) ?? detail.versions[detail.versions.length - 1];
  const latest = detail.versions[detail.versions.length - 1];
  const title = titleOf(current.content);
  const busy = Boolean(working);
  const locked = busy || detail.archived;

  const run = async (label: string, fn: () => Promise<GeneratedCreative>) => {
    try {
      setWorking(label);
      const out = await fn();
      toast.success(out.review?.approved ? `Versão ${out.version} pronta e conferida.` : `Versão ${out.version} pronta. Veja o que a Eco-IA apontou.`);
      setInstruction("");
      navigate(CRIATIVO_PAGE(out.id), { replace: true });
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setWorking(null);
    }
  };

  const archive = async () => {
    try {
      setWorking("Arquivando");
      await archiveCreativeApi(current.id);
      toast.success("Criativo arquivado.");
      navigate(CRIATIVOS_PAGE(), { replace: true });
    } catch (e: any) {
      toast.error(e?.message);
      setWorking(null);
    }
  };

  const download = () => downloadCreative(current.id, creativeFileName(title, current.version)).catch((e) => toast.error(e?.message));

  return (
    <div className="cmpPage">
      <main className="cmpPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate("/") },
            { label: "Campanhas", onClick: () => navigate(CAMPANHAS_PAGE()) },
            { label: "Criativos", onClick: goBack },
            { label: title },
          ]}
          title={title}
          subtitle={`Versão ${current.version} de ${latest.version} · ${current.kindLabel} · ${current.format === "STORY" ? "Stories (9:16)" : "Feed (4:5)"}`}
          actions={
            <>
              <button type="button" className="ecoBtn" onClick={download} disabled={busy}>
                <EcoIcon name="download" size={18} />
                Baixar
              </button>
              {!detail.archived ? (
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setUseAsAd(true)} disabled={busy}>
                  <EcoIcon name="megaphone" size={18} />
                  Usar no anúncio
                </button>
              ) : null}
            </>
          }
        />

        {detail.archived ? (
          <p className="cmpNotice cmpNotice--info">
            <EcoIcon name="alertTriangle" size={16} />
            <span>Criativo arquivado: dá para ver e baixar, mas não gera versão nova.</span>
          </p>
        ) : null}

        <div className="crvStudio">
          <section className="ecoCard cmpPanel crvStudio__canvas">
            <CreativeImage
              id={current.id}
              className={`crvStudio__img${current.format === "STORY" ? " crvStudio__img--story" : ""}`}
              alt={`Criativo ${title}, versão ${current.version}`}
            />
            <ReviewBox review={current.review} />
            {current.instruction ? (
              <p className="cmpSub">
                <strong>Pedido desta versão:</strong> {current.instruction}
              </p>
            ) : null}
            <p className="cmpSub">
              {current.createdByName ? `Por ${current.createdByName} · ` : ""}
              {fmtDateTime(current.createdAt)}
              {current.campaign ? (
                <>
                  {" · "}
                  <button type="button" className="ecoLinkBtn" onClick={() => navigate(CAMPANHA_PAGE(current.campaign!.id))}>
                    {current.campaign.name}
                  </button>
                </>
              ) : null}
            </p>
          </section>

          <div className="crvStudio__side">
            <section className="ecoCard cmpPanel">
              <h2 className="cmpPanel__title">Versões</h2>
              <ol className="crvVersions">
                {[...detail.versions].reverse().map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      className={`crvVersion${v.id === current.id ? " is-active" : ""}`}
                      aria-current={v.id === current.id ? "true" : undefined}
                      onClick={() => navigate(CRIATIVO_PAGE(v.id), { replace: true })}
                    >
                      <CreativeImage id={v.id} className="crvVersion__img" alt="" />
                      <span>
                        <span className="crvVersion__title">
                          v{v.version} · {v.kindLabel}
                        </span>
                        <span className="cmpSub">{fmtDateTime(v.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>

            {!detail.archived ? (
              <section className="ecoCard cmpPanel">
                <EcoSegmented label="Ferramenta" options={TOOLS} value={tool} onChange={setTool} />
                {working ? <GeneratingNotice label={working} /> : null}

                {tool === "ajustar" ? (
                  <div className="crvTool">
                    <label className="ecoField">
                      <span className="ecoLabel">O que mudar na imagem</span>
                      <textarea
                        className="ecoInput cmpTextarea"
                        rows={3}
                        maxLength={600}
                        disabled={locked}
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        placeholder="Ex.: Troque a moto por uma bicicleta e deixe o céu mais azul."
                      />
                    </label>
                    <div className="aiDrawer__starters">
                      {ADJUST_SHORTCUTS.map((s) => (
                        <button key={s} type="button" className="aiChip" disabled={locked} onClick={() => setInstruction(s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="cmpActions">
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--primary"
                        disabled={locked || !instruction.trim()}
                        onClick={() => run("Ajustando a imagem", () => adjustCreativeApi(current.id, instruction.trim()))}
                      >
                        Ajustar
                      </button>
                    </div>
                  </div>
                ) : null}

                {tool === "textos" ? (
                  <div className="crvTool">
                    <p className="cmpHint">A foto e o layout ficam; só os textos mudam.</p>
                    <CreativeContentForm value={texts} onChange={setTexts} disabled={locked} />
                    <div className="cmpActions">
                      <button type="button" className="ecoBtn" disabled={locked} onClick={() => setTexts(current.content)}>
                        Desfazer as mudanças
                      </button>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--primary"
                        disabled={locked}
                        onClick={() => run("Trocando os textos", () => changeTextsApi(current.id, cleanContent(texts)))}
                      >
                        Trocar os textos
                      </button>
                    </div>
                  </div>
                ) : null}

                {tool === "mais" ? (
                  <div className="crvTool">
                    <div className="crvMore">
                      <div>
                        <strong>Versão para Stories</strong>
                        <p className="cmpHint">A mesma arte na vertical (9:16), com as margens que o Instagram e o Facebook pedem.</p>
                      </div>
                      <button
                        type="button"
                        className="ecoBtn"
                        disabled={locked || current.format === "STORY"}
                        title={current.format === "STORY" ? "Esta versão já é para Stories." : undefined}
                        onClick={() => run("Gerando a versão para Stories", () => storiesCreativeApi(current.id))}
                      >
                        <EcoIcon name="layers" size={16} />
                        Gerar Stories
                      </button>
                    </div>
                    <div className="crvMore crvMore--stack">
                      <div>
                        <strong>Refazer do zero</strong>
                        <p className="cmpHint">Desenha de novo a partir do criativo padrão, com os mesmos textos. Mude a foto se quiser.</p>
                      </div>
                      <textarea
                        className="ecoInput cmpTextarea"
                        rows={3}
                        maxLength={600}
                        aria-label="Como é a foto"
                        disabled={locked}
                        value={photoPrompt}
                        onChange={(e) => setPhotoPrompt(e.target.value)}
                      />
                      <div className="cmpActions">
                        <button
                          type="button"
                          className="ecoBtn"
                          disabled={locked}
                          onClick={() => run("Refazendo o criativo", () => redoCreativeApi(current.id, photoPrompt.trim() || undefined))}
                        >
                          <EcoIcon name="refresh" size={16} />
                          Refazer
                        </button>
                      </div>
                    </div>
                    <div className="crvMore">
                      <div>
                        <strong>Arquivar</strong>
                        <p className="cmpHint">Sai da biblioteca com todas as versões. Anúncio que já usa a imagem não muda.</p>
                      </div>
                      <button type="button" className="ecoBtn" disabled={busy} onClick={() => setConfirmArchive(true)}>
                        Arquivar
                      </button>
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        </div>
      </main>

      {useAsAd ? (
        <UseCreativeAsAdModal
          creativeId={current.id}
          defaultCampaignId={current.campaign?.id ?? null}
          onClose={() => setUseAsAd(false)}
          onDone={(campaignId) => {
            setUseAsAd(false);
            navigate(CAMPANHA_PAGE(campaignId));
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={confirmArchive}
        title="Arquivar o criativo?"
        message="Ele sai da biblioteca com todas as versões. Os anúncios que já usam a imagem continuam iguais."
        confirmLabel="Arquivar"
        busy={busy}
        onConfirm={archive}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
};

export default CriativoPage;
